import { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DialCodeSelect } from '@/components/UI/GeoSelect';
import { useGetCountriesQuery } from '@/redux/internal-services/geoApi';
import { splitPhone, normalizeDialCode } from '@/utils/phone';
import { apiErrorMessage } from '@/utils/apiError';
import {
  useListInvitationsQuery,
  useSendInvitationMutation,
  useRevokeInvitationMutation,
  useListTenantRolesQuery,
} from '@/redux/internal-services/onboardingApi';
import { BackIcon } from './StepShell';
import type { StepHandle, SubmitResult } from './stepHandle';
import { Button } from '@/components/UI/Button';
import { IconButton } from '@/components/UI/IconButton';
import { Modal } from '@/components/UI/Modal';
import { CloseIcon } from '@/components/UI/icons';
import { fieldClass } from '@/components/UI/fieldStyles';
import { cn } from '@/utils/cn';

/** The teammate being filled in (not yet invited): kept by the wizard so a detour to another step doesn't lose it. */
export interface TeamDraft {
  name: string;
  email: string;
  countryCode: string;
  phone: string;
  roleIds: string[];
}

interface Props {
  /** The draft the wizard kept when this step was last left. */
  initialDraft?: TeamDraft | null;
  /** Called on every change to the draft (and with an empty one once the invitation is sent). */
  onDraftChange?: (draft: TeamDraft) => void;
  onBack?: () => void;
  onLaunch: () => void;
  onSkip?: () => void;
  launching?: boolean;
  disabled?: boolean;
}

interface InvitationRow {
  invitation_id?: string;
  invitee_name?: string;
  invitee_email?: string;
  roles?: string[];
  status?: string;
}

/**
 * Guided-setup "Team Onboarding" step — invite team members (reuses the invitations API) and
 * launch the dashboard. Each invite is sent immediately; "Launch Dashboard" finalises setup.
 */
// Shown nowhere: a cancelled or lapsed invitation is not part of the team the user is building.
const HIDDEN_STATUSES = new Set(['Revoked', 'Expired']);
// The API refuses to cancel an invitation that was already accepted (or cancelled); anything else can go.
const canRemove = (inv: { invitation_id?: string; status?: string }) => Boolean(inv.invitation_id) && inv.status !== 'Accepted';

const TeamOnboardingStep = forwardRef<StepHandle, Props>(({ initialDraft, onDraftChange, onBack, onLaunch, onSkip, launching, disabled = false }, ref) => {
  const { t } = useTranslation();
  const { data: invitationsData, isLoading: invitationsLoading } = useListInvitationsQuery();
  const { data: tenantRolesData } = useListTenantRolesQuery();
  const { data: countriesData } = useGetCountriesQuery();
  const [sendInvitation, { isLoading: sending }] = useSendInvitationMutation();
  const [revokeInvitation, { isLoading: removing }] = useRevokeInvitationMutation();

  const invitations = ((invitationsData?.data ?? []) as InvitationRow[]).filter(
    (i) => !HIDDEN_STATUSES.has(i.status ?? '')
  );
  const [pendingRemove, setPendingRemove] = useState<InvitationRow | null>(null);
  // What became of the agent's last remove request, reported back through getState() (see removeByIdentifier).
  const lastActionRef = useRef<{ request_id?: string; action: string; ok: boolean; reason?: string; message?: string } | null>(null);
  const roles = useMemo(
    () => (tenantRolesData?.data ?? []).filter((r) => r.visibility),
    [tenantRolesData]
  );
  const roleName = (id: string) => roles.find((r) => r.tenant_role_id === id)?.name ?? id;

  const [name, setName] = useState(initialDraft?.name ?? '');
  const [email, setEmail] = useState(initialDraft?.email ?? '');
  const [countryCode, setCountryCode] = useState(initialDraft?.countryCode ?? '+1');
  const [phone, setPhone] = useState(initialDraft?.phone ?? '');
  const [roleIds, setRoleIds] = useState<string[]>(initialDraft?.roleIds ?? []);
  const [error, setError] = useState('');

  // Synchronous mirror so voice can fill the draft fields one-by-one and then add, reading the
  // freshest values even if the actuators land in the same tick.
  const draftRef = useRef({ name, email, phone, roleIds });  // seeded from the restored draft via the state above
  useEffect(() => { draftRef.current = { name, email, phone, roleIds }; }, [name, email, phone, roleIds]);

  // Hand the draft to the wizard on every change, so it is still there if the user (or the agent) detours to another
  // step and comes back; the reset after a sent invitation reaches it the same way.
  const onDraftChangeRef = useRef(onDraftChange);
  useEffect(() => { onDraftChangeRef.current = onDraftChange; });
  useEffect(() => {
    onDraftChangeRef.current?.({ name, email, countryCode, phone, roleIds });
  }, [name, email, countryCode, phone, roleIds]);

  const toggleRole = (id: string) =>
    setRoleIds((prev) => (prev.includes(id) ? prev.filter((r) => r !== id) : [...prev, id]));

  // Send an invitation from the current draft. Shared by the visible button and the voice agent. The voice agent passes
  // a request id, and what happened to THAT request is reported in the step state (lastActionResult), so the agent can
  // tell "sent" from "refused" (no role, a duplicate, an API error) instead of assuming the send worked.
  const sendCurrent = async (requestId?: string): Promise<SubmitResult> => {
    const report = (result: SubmitResult): SubmitResult => {
      if (requestId !== undefined) lastActionRef.current = { request_id: requestId, action: 'add_team_member', ...result };
      return result;
    };
    const d = draftRef.current;
    if (!d.email.trim()) {
      setError(t('onboarding.steps.emailRequired'));
      return report({ ok: false, reason: 'validation', message: 'Email address is required.' });
    }
    if (d.roleIds.length === 0) {
      setError(t('onboarding.steps.selectAtLeastOneRole'));
      return report({ ok: false, reason: 'validation', message: 'At least one role is required.' });
    }
    setError('');
    try {
      await sendInvitation({
        invitee_name: d.name.trim() || undefined,
        invitee_email: d.email.trim(),
        invitee_phone: d.phone.trim() ? `${countryCode}${d.phone.trim()}` : undefined,
        roles: d.roleIds,
      }).unwrap();
      draftRef.current = { name: '', email: '', phone: '', roleIds: [] };
      setName('');
      setEmail('');
      setPhone('');
      setRoleIds([]);
      return report({ ok: true });
    } catch (err) {
      setError(apiErrorMessage(err, t, 'Failed to add team member. Please try again.'));
      return report({ ok: false, reason: 'transient', message: 'Failed to add team member.' });
    }
  };

  const handleAdd = () => { void sendCurrent(); };

  // Cancel an invitation on the agent's behalf ("remove Jake"): the person's email or exact name picks it, and
  // anything but exactly one removable match is refused so a vague name never removes the wrong person.
  const removeByIdentifier = async (who: string, requestId?: string): Promise<SubmitResult> => {
    const report = (result: SubmitResult): SubmitResult => {
      lastActionRef.current = { request_id: requestId, action: 'remove_team_member', ...result };
      return result;
    };
    const q = who.trim().toLowerCase();
    const matches = q
      ? invitations.filter(
          (i) => canRemove(i) && (i.invitation_id?.toLowerCase() === q || i.invitee_email?.toLowerCase() === q || i.invitee_name?.toLowerCase() === q),
        )
      : [];
    if (matches.length !== 1) {
      const message = matches.length ? 'More than one invited person matches.' : 'No invited person matches (or they already joined).';
      return report({ ok: false, reason: 'validation', message });
    }
    try {
      await revokeInvitation(matches[0].invitation_id as string).unwrap();
      setError('');
      return report({ ok: true });
    } catch (err) {
      setError(apiErrorMessage(err, t, 'Failed to remove the invitation. Please try again.'));
      return report({ ok: false, reason: 'transient', message: 'Failed to remove the invitation.' });
    }
  };

  const inviteLabel = (inv: InvitationRow) => inv.invitee_name || inv.invitee_email || 'this person';

  // Cancel a sent invitation (DELETE /invitations/{id}); the list refetches through the Invitation tag.
  const confirmRemove = async () => {
    const inv = pendingRemove;
    if (!inv?.invitation_id) { setPendingRemove(null); return; }
    try {
      await revokeInvitation(inv.invitation_id).unwrap();
      setPendingRemove(null);
      setError('');
    } catch (err) {
      setPendingRemove(null);
      setError(apiErrorMessage(err, t, 'Failed to remove the invitation. Please try again.'));
    }
  };

  const dialCodes = countriesData?.data?.map((c) => c.dial_code) ?? [];

  // Voice actuators — fill the visible draft fields one-by-one, then add via addTeamMember().
  const setFieldById = (id: string, value: string) => {
    if (id === 'name') { draftRef.current = { ...draftRef.current, name: value }; setName(value); }
    else if (id === 'email') { draftRef.current = { ...draftRef.current, email: value }; setEmail(value); }
    else if (id === 'phoneCountryCode') {
      // Agent set the dial code separately → drive the dropdown directly.
      const norm = normalizeDialCode(value);
      if (norm) setCountryCode(norm);
    }
    else if (id === 'phone') {
      // Agent passes the local number (or a full international one as a fallback). Separate the
      // country code from the local part so the dropdown + number field are populated correctly.
      const { countryCode: cc, number: num } = splitPhone(value, dialCodes);
      if (cc) setCountryCode(cc);
      draftRef.current = { ...draftRef.current, phone: num };
      setPhone(num);
    }
  };

  const setRole = (rName: string, selected: boolean) => {
    const id = roles.find((r) => r.name.toLowerCase() === rName.trim().toLowerCase())?.tenant_role_id;
    if (!id) { setError(`Unknown role "${rName}".`); return; }
    const has = draftRef.current.roleIds.includes(id);
    const next = selected
      ? (has ? draftRef.current.roleIds : [...draftRef.current.roleIds, id])
      : draftRef.current.roleIds.filter((r) => r !== id);
    draftRef.current = { ...draftRef.current, roleIds: next };
    setRoleIds(next);
  };

  useImperativeHandle(ref, (): StepHandle => ({
    getState: () => ({
      // id + status let the backend refuse someone who already joined (status "Accepted") without trying, and target
      // an invitee by id even when they have no email.
      invitations: invitations.map((i) => ({ id: i.invitation_id, name: i.invitee_name, email: i.invitee_email, status: i.status, roles: (i.roles ?? []).map(roleName) })),
      lastActionResult: lastActionRef.current,
      // True until the first invitations response lands: an empty list then means "not known yet", not "nobody
      // invited" — the agent reads this state straight after arriving on the step, before the list has loaded.
      invitationsLoading,
      availableRoles: roles.map((r) => r.name),
      draft: { name, email, countryCode, phone, roleIds: roleIds.map(roleName) },
    }),
    // Invitations are sent as they're added, so there's nothing to persist here. Finishing the
    // wizard (launch) is handled by the page's nav.goNext/nav.launch, not by submit, to avoid a
    // double navigation when the agent says "next" on the final step.
    submit: async () => ({ ok: true }),
    setField: setFieldById,
    setRole,
    addTeamMember: (requestId?: string) => sendCurrent(requestId),
    removeTeamMember: removeByIdentifier,
  }));

  const inputCls = fieldClass();

  return (

    <div className="card-surface p-4 sm:p-8">
      <h2 className="text-h3 text-ui-text mb-1">Team Onboarding</h2>
      <p className="text-ui-text2 text-sm mb-6">Final step — invite your team members</p>


      {invitations.length > 0 && (
        <div className="mb-6">
          <p className="text-sm font-medium text-ui-text mb-2">Added Team Members</p>
          <div className="space-y-2">
            {invitations.map((inv, idx) => (
              <div key={inv.invitation_id ?? idx} className="flex items-center justify-between border border-ui-border rounded-card px-4 py-3">
                <div>
                  <p className="text-sm font-medium text-ui-text">{inv.invitee_name || inv.invitee_email}</p>
                  {inv.invitee_name && <p className="text-xs text-ui-text3">{inv.invitee_email}</p>}
                </div>
                <div className="flex items-center gap-2">
                  {inv.roles && inv.roles.length > 0 && (
                    <div className="flex flex-wrap gap-1 justify-end">
                      {inv.roles.map((rid) => (
                        <span key={rid} className="px-2.5 py-1 bg-accent-soft text-accent-text text-xs font-medium rounded-full">
                          {roleName(rid)}
                        </span>
                      ))}
                    </div>
                  )}
                  {canRemove(inv) && (
                    <IconButton size="sm" label={`Remove ${inviteLabel(inv)}`} onClick={() => setPendingRemove(inv)}>
                      <CloseIcon />
                    </IconButton>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <p className="text-sm font-medium text-ui-text mb-3">Add new Team member</p>

      <div className="mb-3">
        <label className="block text-sm text-ui-text2 mb-1.5">Name</label>
        <input type="text" placeholder="Enter team member name" value={name} onChange={(e) => setName(e.target.value)} className={inputCls} />
      </div>

      <div className="mb-3">
        <label className="block text-sm text-ui-text2 mb-1.5">Email Address</label>
        <input type="email" placeholder="name@company.com" value={email} onChange={(e) => setEmail(e.target.value)} className={inputCls} />
      </div>

      <div className="mb-3">
        <label className="block text-sm text-ui-text2 mb-1.5">Mobile Phone Number</label>
        <div className="flex gap-2">
          <DialCodeSelect value={countryCode} onChange={setCountryCode} className="w-28 sm:w-32" />
          <input type="tel" placeholder="Phone number" value={phone} onChange={(e) => setPhone(e.target.value)} className={cn(fieldClass(), 'min-w-0 flex-1')} />
        </div>
      </div>

      <div className="mb-4">
        <label className="block text-sm text-ui-text2 mb-1.5">Roles</label>
        <div className="flex flex-wrap gap-2">
          {roles.map((r) => (
            <button
              key={r.tenant_role_id}
              type="button"
              onClick={() => toggleRole(r.tenant_role_id)}
              disabled={disabled}
              className={`px-3 py-1.5 rounded-full text-sm font-medium transition ${
                roleIds.includes(r.tenant_role_id) ? 'bg-accent-fill text-on-accent' : 'bg-ui-subtle text-ui-text2 hover:bg-ui-hover'
              } disabled:pointer-events-none disabled:opacity-60`}
            >
              {r.name}
            </button>
          ))}
        </div>
      </div>

      {error && <p className="text-danger-text text-sm mb-3">{error}</p>}

      <Button type="button"
        onClick={handleAdd}
        disabled={sending || disabled}
        variant="secondary" size="lg" className="w-full mb-6">
        <svg viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
          <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
        </svg>
        {sending ? 'Adding...' : 'Add Team Member'}
      </Button>

      <div className="flex gap-3">
        {onBack && (
          <Button type="button" onClick={onBack} disabled={disabled} variant="outline" size="lg" className="flex-1">
            <BackIcon />
            Back
          </Button>
        )}
        <Button type="button"
          onClick={onLaunch}
          disabled={launching || disabled}
          size="lg" className="flex-1">
          {launching ? 'Saving...' : 'Next'}
          <svg viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
            <path fillRule="evenodd" d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z" clipRule="evenodd" />
          </svg>
        </Button>
      </div>

      {onSkip && (
        <Button type="button" onClick={onSkip} disabled={disabled} variant="link" className="w-full mt-3">
          Skip this step
        </Button>
      )}

      {pendingRemove && (
        <Modal
          title="Remove team member?"
          onClose={() => setPendingRemove(null)}
          onSave={() => { void confirmRemove(); }}
          saving={removing}
          saveLabel="Remove"
          danger
          centered
        >
          <p className="text-sm text-ui-text2">
            {inviteLabel(pendingRemove)}'s invitation will be cancelled, and they won't be able to join with it.
          </p>
        </Modal>
      )}
    </div>
  );
});

TeamOnboardingStep.displayName = 'TeamOnboardingStep';

export default TeamOnboardingStep;
